import { motion } from 'framer-motion';
import { usePathname, useRouter } from 'next/navigation';
import React from 'react';

import { Link } from '@/components/link';
import { openNavStyles } from '@/components/modules/app-navigation/open';
import { AppNavigationSearchHistory } from '@/components/modules/app-navigation/search-history';
import { closedNavIconVariants } from '@/components/modules/app-navigation/utils/transitions';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { NotificationDot } from '@/components/ui/notification-dot';
import { useFeatureNotifications } from '@/hooks/use-feature-notifications';
import { AppNavItem } from '@/lib/constants';
import { isNavItemActive } from '@/utils/helpers/page-urls';
import { tv } from '@/utils/styles';

export interface ClosedItemDropdownProps {
  item: AppNavItem;
  isOpen?: boolean;
  isTouchDevice: boolean;
  openDropdownMenuId: string | null;
  onSelect: (value: string | null) => void;
  onPointerEnter: (event: React.PointerEvent<HTMLButtonElement | HTMLDivElement>) => void;
  onPointerLeave: (event: React.PointerEvent<HTMLButtonElement | HTMLDivElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement | HTMLDivElement>) => void;
}

const ClosedItemDropdown = ({
  isOpen,
  item,
  isTouchDevice,
  openDropdownMenuId,
  onSelect,
  onPointerEnter,
  onPointerLeave,
  onKeyDown,
}: ClosedItemDropdownProps) => {
  const router = useRouter();
  const pathname = usePathname() || '';

  const { isFeatureSeen } = useFeatureNotifications(item.notification?.id);

  const IconComponent = item.icon;
  const dropdownMenuProps = !isTouchDevice && { modal: false, open: openDropdownMenuId === item.id };
  const parentIsActive = item.subItems.some((subItem) => isNavItemActive({ asPath: pathname, itemUrl: subItem.url }));

  const { dropdownMenuContent, dropdownMenuBg, dropdownMenuItem, dropdownMenuLink, notificationWrapper } =
    closedItemStyles();

  const { icon, trigger, triggerBg } = openNavStyles();

  return (
    <DropdownMenu key={item.id} modal={false} {...dropdownMenuProps}>
      <motion.div
        variants={closedNavIconVariants}
        animate={isOpen ? 'hidden' : 'visible'}
        initial={isOpen ? 'hidden' : 'visible'}
      >
        <DropdownMenuTrigger
          className={trigger()}
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
          onKeyDown={onKeyDown}
        >
          <IconComponent
            className={icon({
              className: parentIsActive ? 'text-white' : '',
            })}
          />
          {item.notification && !isFeatureSeen ? (
            <div className={notificationWrapper()}>
              <NotificationDot tooltipContent={item.notification.tooltipContent} />
            </div>
          ) : null}
          <span
            className={triggerBg({
              isActive: parentIsActive,
            })}
          />
        </DropdownMenuTrigger>
      </motion.div>
      {item.subItems && (
        <DropdownMenuContent
          className={dropdownMenuContent()}
          side="right"
          align="start"
          sideOffset={6}
          alignOffset={-36} // Align the trigger with the first item in the sub items
          onCloseAutoFocus={(event) => event?.preventDefault()}
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
          onEscapeKeyDown={() => onSelect(null)}
          loop
        >
          <DropdownMenuLabel>{item.label}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <ul>
              {item.subItems.map((subItem, index) => {
                const subItemIsActive = isNavItemActive({ asPath: pathname, itemUrl: subItem.url });

                return (
                  <li key={index}>
                    <DropdownMenuItem
                      className={dropdownMenuItem()}
                      onKeyDown={(event) => {
                        if (['Enter', ' '].includes(event.key)) {
                          event.preventDefault();
                          router.push(subItem.url);
                        }
                      }}
                    >
                      <Link
                        href={subItem.url}
                        className={dropdownMenuLink({
                          isActive: subItemIsActive,
                        })}
                      >
                        {subItem.label}
                      </Link>
                      <span
                        className={dropdownMenuBg({
                          isActive: subItemIsActive,
                        })}
                      />
                    </DropdownMenuItem>
                  </li>
                );
              })}
              {item.id === 'routing' && <AppNavigationSearchHistory isClosed limit={5} />}
            </ul>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  );
};

export const closedItemStyles = tv({
  slots: {
    dropdownMenuContent: 'border-grey-300 bg-white shadow-none',
    dropdownMenuBg:
      'dropdown-menu-bg pointer-events-none absolute bottom-0 left-0 right-0 top-0 z-1 scale-75 rounded bg-grey-200 opacity-0 transition-all',
    dropdownMenuItem:
      'block hover:bg-transparent [&_.dropdown-menu-bg]:hover:scale-100 [&_.dropdown-menu-bg]:hover:opacity-100',
    dropdownMenuLink: 'relative z-2 block',
    notificationWrapper: 'absolute left-1 top-2 z-8',
  },
  variants: {
    isActive: {
      true: {
        dropdownMenuBg: 'scale-100 opacity-100',
      },
    },
  },
});

export { ClosedItemDropdown };
